import ReportHeader from "./ReportHeader";
import ReportStyle from "./ReportStyle";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import { tokens } from "../../theme";
import {
  ACTIVITY_LABEL,
  DISCIPLINE_LABEL,
  LEVEL_REPORT_LABEL,
} from "../../utils/mapers";
import {
  formatTitle,
  isMissingValue,
  normalizeContentFilter,
  normalizeText,
  plural,
  universityImage,
  valueOrDash,
} from "../../utils/utils";

const colors = tokens();

const styles = StyleSheet.create({
  page: {
    paddingTop: 58,
    paddingBottom: 34,
    paddingHorizontal: 0,
    fontSize: 10,
    fontFamily: "Helvetica",
    color: colors.primary[100],
  },

  content: {
    paddingHorizontal: 30,
    paddingTop: 12,
  },

  pageNumber: {
    position: "absolute",
    bottom: 9.5,
    right: 32,
    fontSize: 9,
    color: colors.primary[200],
  },

  subtitle: {
    fontSize: 10,
    marginBottom: 8,
    color: colors.primary[100],
  },

  section: {
    marginBottom: 12,
    paddingBottom: 2,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    borderBottomStyle: "solid",
  },

  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 8,
    color: colors.green[200],
  },

  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 4,
    marginBottom: 6,
  },

  summaryCard: {
    width: "31.5%",
    minHeight: 46,
    marginRight: 6,
    marginBottom: 6,
    paddingVertical: 7,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderStyle: "solid",
    borderRadius: 6,
    backgroundColor: colors.primary[200],
  },

  headerSummaryCard: {
    width: 88,
    minHeight: 40,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 6,
  },

  summaryCardValue: {
    fontSize: 15,
    fontWeight: "bold",
    color: colors.green[200],
    marginBottom: 2,
  },

  summaryCardLabel: {
    fontSize: 8.5,
    lineHeight: 1.2,
    color: colors.primary[100],
  },

  table: {
    marginBottom: 8,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderStyle: "solid",
    borderRadius: 4,
    overflow: "hidden",
  },

  tableTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 4,
    color: colors.primary[100],
  },

  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    borderBottomStyle: "solid",
  },

  tableHeader: {
    backgroundColor: colors.primary[200],
  },

  tableCell: {
    flex: 1,
    paddingVertical: 4,
    paddingHorizontal: 5,
    fontSize: 8.5,
    lineHeight: 1.2,
  },

  tableCellSmall: {
    width: 92,
    paddingVertical: 4,
    paddingHorizontal: 5,
    fontSize: 8.5,
    textAlign: "right",
  },

  tableCellHeader: {
    fontWeight: "bold",
    color: colors.primary[100],
  },

  twoCols: {
    flexDirection: "row",
    gap: 12,
  },

  col: {
    flex: 1,
  },

  entityCard: {
    marginBottom: 8,
    padding: 8,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderStyle: "solid",
    borderRadius: 6,
  },

  entityHeader: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 6,
    alignItems: "center",
  },

  logoBox: {
    width: 42,
    height: 42,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderStyle: "solid",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.grey[400],
    overflow: "hidden",
  },

  logo: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },

  logoPlaceholder: {
    fontSize: 15,
    fontWeight: "bold",
    color: colors.grey[500],
  },

  entityTitleBox: {
    flex: 1,
  },

  entityTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: colors.primary[100],
    marginBottom: 3,
  },

  tagRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },

  tag: {
    paddingVertical: 2,
    paddingHorizontal: 5,
    borderRadius: 3,
    fontSize: 7.5,
    color: colors.primary[100],
    backgroundColor: colors.grey[100],
  },

  entityBody: {
    marginTop: 2,
  },

  empty: {
    fontFamily: "Helvetica",
    color: colors.grey[600],
  },
});

const fechaEmision = new Date().toLocaleDateString("es-CL", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function splitValues(value) {
  return String(value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter((item) => !isMissingValue(item));
}

function sortRows(rows) {
  return [...rows].sort((a, b) => {
    const diff = Number(b.value ?? b.count ?? 0) - Number(a.value ?? a.count ?? 0);
    return (
      diff ||
      String(a.label ?? a.id ?? "").localeCompare(
        String(b.label ?? b.id ?? ""),
        "es"
      )
    );
  });
}

function countBy(rows, keyFn) {
  const map = new Map();

  rows.forEach((row) => {
    const key = String(keyFn(row) ?? "").trim();

    if (isMissingValue(key)) return;

    map.set(key, (map.get(key) ?? 0) + 1);
  });

  return sortRows(
    Array.from(map.entries()).map(([label, value]) => ({ label, value }))
  );
}

function countMany(rows, keysFn) {
  const map = new Map();

  rows.forEach((row) => {
    Array.from(new Set(keysFn(row)))
      .map((item) => String(item ?? "").trim())
      .filter((item) => !isMissingValue(item))
      .forEach((item) => map.set(item, (map.get(item) ?? 0) + 1));
  });

  return sortRows(
    Array.from(map.entries()).map(([label, value]) => ({ label, value }))
  );
}

function hasUniversity(participante, universityId) {
  const id = String(universityId ?? "").trim().toUpperCase();

  return (participante?.universidad_keys ?? []).some(
    (key) => String(key ?? "").trim().toUpperCase() === id
  );
}

function getActivityLabel(bucket, value) {
  const labels = ACTIVITY_LABEL[bucket] ?? ACTIVITY_LABEL.other;

  return plural(value, labels.singular, labels.plural);
}

function activityRowsFromEvents(events = []) {
  return sortRows(
    Object.entries(
      events.reduce((acc, event) => {
        const bucket = ACTIVITY_LABEL[event?.bucket] ? event.bucket : "other";
        acc[bucket] = (acc[bucket] ?? 0) + 1;
        return acc;
      }, {})
    ).map(([bucket, value]) => ({
      label: getActivityLabel(bucket, value),
      value,
    }))
  );
}

function activityRowsForParticipants(
  events = [],
  participantes = [],
  allEvents = events
) {
  const ids = new Set(
    participantes
      .map((participante) => String(participante?.id ?? "").trim())
      .filter(Boolean)
  );

  const totals = allEvents.reduce((acc, event) => {
    const bucket = ACTIVITY_LABEL[event?.bucket] ? event.bucket : "other";
    acc[bucket] = (acc[bucket] ?? 0) + 1;
    return acc;
  }, {});

  return sortRows(
    Object.values(
      events.reduce((acc, event) => {
        const bucket = ACTIVITY_LABEL[event?.bucket] ? event.bucket : "other";

        const attendees = (event?.attendees ?? [])
          .map((attendee) => String(attendee?.id ?? "").trim())
          .filter((id) => id && ids.has(id));

        if (!attendees.length) return acc;

        if (!acc[bucket]) {
          acc[bucket] = {
            bucket,
            attendedActivities: 0,
            attendees: new Set(),
          };
        }

        acc[bucket].attendedActivities += 1;
        attendees.forEach((id) => acc[bucket].attendees.add(id));

        return acc;
      }, {})
    ).map(({ bucket, attendedActivities, attendees }) => {
      const totalActivities = totals[bucket] ?? 0;

      return {
        label: getActivityLabel(bucket, attendedActivities),
        value: attendedActivities,
        displayValue: `${attendedActivities}/${totalActivities} ${plural(
          totalActivities,
          "actividad",
          "actividades"
        )}`,
        attendance: `${attendees.size} ${plural(
          attendees.size,
          "participante",
          "participantes"
        )}`,
      };
    })
  );
}


function standardRows(participantes = [], field, keysField) {
  const map = new Map();

  participantes.forEach((participante) => {
    const labels = String(participante?.[field] ?? "")
      .split(/,\s*(?=Estándar\s+[^:]+:)/gi)
      .map((label) => label.trim())
      .filter((label) => !isMissingValue(label));

    const keys = Array.isArray(participante?.[keysField])
      ? participante[keysField]
      : [];

    const counted = new Set();

    labels.forEach((label, index) => {
      const key = String(keys[index] ?? label).trim();

      if (!key || counted.has(key)) return;

      counted.add(key);

      const row = map.get(key) ?? {
        key,
        label,
        value: 0,
      };

      row.value += 1;
      map.set(key, row);
    });
  });

  return Array.from(map.values())
    .sort((a, b) =>
      String(a.key).localeCompare(String(b.key), "es", {
        numeric: true,
        sensitivity: "base",
      })
    )
    .map(({ key, ...row }) => row);
}

function experienceRows(participantes = []) {
  return countBy(
    participantes,
    (participante) =>
      LEVEL_REPORT_LABEL[normalizeText(participante?.nivel)] ?? null
  );
}

function SummaryCard({ value, label, style }) {
  return (
    <View style={[styles.summaryCard, style]} wrap={false}>
      <Text style={styles.summaryCardValue}>{value}</Text>
      <Text style={styles.summaryCardLabel}>{label}</Text>
    </View>
  );
}

function SectionTable({
  title,
  rows,
  valueLabel = "N°",
  attendanceLabel = null,
}) {
  const safeRows = rows.filter(
    (row) => Number(row.value ?? row.count ?? 0) > 0
  );

  const firstRow = safeRows[0];
  const remainingRows = safeRows.slice(1);

  const renderRow = (row, index) => (
    <View
      key={`${title}-${row.label ?? row.id}-${index}`}
      style={styles.tableRow}
      wrap={false}
    >
      <Text style={styles.tableCell}>
        {valueOrDash(row.label ?? row.id)}
      </Text>

      <Text style={styles.tableCellSmall}>
        {valueOrDash(
          row.displayValue ?? row.value ?? row.count
        )}
      </Text>

      {attendanceLabel ? (
        <Text style={styles.tableCellSmall}>
          {valueOrDash(row.attendance)}
        </Text>
      ) : null}
    </View>
  );

  return (
    <View>
      <Text
        style={styles.tableTitle}
        minPresenceAhead={55}
      >
        {title}
      </Text>

      {safeRows.length ? (
        <View style={styles.table}>
          <View wrap={false}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text
                style={[
                  styles.tableCell,
                  styles.tableCellHeader,
                ]}
              >
                Categoría
              </Text>

              <Text
                style={[
                  styles.tableCellSmall,
                  styles.tableCellHeader,
                ]}
              >
                {valueLabel}
              </Text>

              {attendanceLabel ? (
                <Text
                  style={[
                    styles.tableCellSmall,
                    styles.tableCellHeader,
                  ]}
                >
                  {attendanceLabel}
                </Text>
              ) : null}
            </View>

            {firstRow ? renderRow(firstRow, 0) : null}
          </View>

          {remainingRows.map((row, index) =>
            renderRow(row, index + 1)
          )}
        </View>
      ) : (
        <Text style={styles.empty}>
          No hay información disponible.
        </Text>
      )}
    </View>
  );
}

function Tags({ items }) {
  const tags = items.filter(Boolean);

  if (!tags.length) return null;

  return (
    <View style={styles.tagRow}>
      {tags.map((tag) => (
        <Text key={tag} style={styles.tag}>
          {tag}
        </Text>
      ))}
    </View>
  );
}

function UniversityCard({
  university,
  participantes,
  eventosConAsistentes,
  eventos,
  showActivities = true,
  isForeign = false,
}) {
  const universityParticipants = participantes.filter((participante) =>
    hasUniversity(participante, university.id)
  );
  const rawUniversityName = valueOrDash(
    university?.universidad_full ?? university?.universidad
  );

  const universityName =
    rawUniversityName.includes(" ") &&
      (rawUniversityName === rawUniversityName.toUpperCase() ||
        rawUniversityName === rawUniversityName.toLowerCase())
      ? formatTitle(rawUniversityName)
      : rawUniversityName;

  const logo = universityImage(rawUniversityName);

  return (
    <View style={styles.entityCard} wrap={false}>
      <View style={styles.entityHeader}>
        <View style={styles.logoBox}>
          {logo ? (
            <Image cache src={logo} style={styles.logo} />
          ) : (
            <Text style={styles.logoPlaceholder}>
              {String(university?.universidad ?? "U").trim().charAt(0)}
            </Text>
          )}
        </View>

        <View style={styles.entityTitleBox}>
          <Text style={styles.entityTitle}>
            {valueOrDash(universityName)}
          </Text>

          {!isForeign ? (
            <Tags
              items={[
                university?.universidad_cuech && "CUECH",
                university?.universidad_cruch && "CRUCH",
              ]}
            />
          ) : null}
        </View>

        <SummaryCard
          value={university?.formadores_qtty ?? universityParticipants.length}
          label="Formadores"
          style={styles.headerSummaryCard}
        />
      </View>

      <View style={styles.entityBody}>
        {/* FILA 1 */}
        <View style={styles.twoCols}>
          {/* Columna izquierda */}
          <View style={styles.col}>
            <SectionTable
              title="Disciplinas"
              rows={countMany(
                universityParticipants,
                (participante) => splitValues(participante?.disciplina)
              ).slice(0, 6)}
              valueLabel="Formadores"
            />

            <SectionTable
              title="Grado académico"
              rows={countBy(
                universityParticipants,
                (participante) => participante?.grado
              ).slice(0, 6)}
              valueLabel="Formadores"
            />
          </View>

          {/* Columna derecha */}
          <View style={styles.col}>
            <SectionTable
              title="Programas o carreras"
              rows={countMany(
                universityParticipants,
                (participante) => splitValues(participante?.programa)
              ).slice(0, 6)}
              valueLabel="Formadores"
            />
          </View>
        </View>

        {/* FILA 2 */}
        <View style={styles.twoCols}>
          <View style={styles.col}>
            <SectionTable
              title="Título profesional"
              rows={countBy(
                universityParticipants,
                (participante) => participante?.titulo_profesional
              ).slice(0, 6)}
              valueLabel="Formadores"
            />
          </View>

          <View style={styles.col}>
            <SectionTable
              title="Experiencia como formador/a"
              rows={experienceRows(universityParticipants)}
              valueLabel="Formadores"
            />
          </View>
        </View>

        {showActivities ? (
          <SectionTable
            title="Participación en actividades"
            rows={activityRowsForParticipants(
              eventosConAsistentes,
              universityParticipants,
              eventos
            )}
            valueLabel="Participación"
            attendanceLabel="Asistencia"
          />
        ) : null}
      </View>
    </View>
  );
}

function GeographicCard({
  area,
  participantes,
  eventosConAsistentes,
  eventos,
  isForeign,
}) {
  const areaParticipants = participantes.filter((participante) =>
    isForeign
      ? normalizeText(participante?.pais) === area.id
      : String(participante?.region_roman ?? "").trim() ===
      String(area?.region_roman ?? "").trim()
  );
  const areaName = isForeign
    ? area?.pais
    : area?.region_name ?? area?.region;
  const areaCode = isForeign
    ? String(areaName ?? "").trim().slice(0, 2).toUpperCase()
    : area?.region_roman;

  return (
    <View style={styles.entityCard} wrap={false}>
      <View style={styles.entityHeader}>
        <View style={styles.logoBox}>
          <Text style={styles.logoPlaceholder}>{valueOrDash(areaCode)}</Text>
        </View>

        <View style={styles.entityTitleBox}>
          <Text style={styles.entityTitle}>{valueOrDash(areaName)}</Text>

          {!isForeign ? (
            <Tags
              items={[
                area?.region_roman && `Región ${area.region_roman}`,
              ]}
            />
          ) : null}
        </View>
      </View>

      <View style={styles.grid}>
        <SummaryCard
          value={area?.formadores_qtty ?? areaParticipants.length}
          label="Formadores"
        />
        <SummaryCard
          value={
            countMany(areaParticipants, (participante) =>
              splitValues(participante?.universidad)
            ).length
          }
          label="Universidades"
        />
      </View>

      <View style={styles.twoCols}>
        <View style={styles.col}>
          <SectionTable
            title="Disciplinas"
            rows={countMany(areaParticipants, (participante) =>
              splitValues(participante?.disciplina)
            ).slice(0, 6)}
            valueLabel="Formadores"
          />
        </View>

        <View style={styles.col}>
          <SectionTable
            title="Programas o carreras"
            rows={countMany(areaParticipants, (participante) =>
              splitValues(participante?.programa)
            )
              .filter(
                (row) => !normalizeText(row.label).includes("postgrado")
              )
              .slice(0, 6)}
            valueLabel="Formadores"
          />
        </View>
      </View>

      <View style={styles.twoCols}>
        <View style={styles.col}>
          <SectionTable
            title="Título profesional"
            rows={countBy(
              areaParticipants,
              (participante) => participante?.titulo_profesional
            ).slice(0, 6)}
            valueLabel="Formadores"
          />
        </View>

        <View style={styles.col}>
          <SectionTable
            title="Grado académico"
            rows={countBy(
              areaParticipants,
              (participante) => participante?.grado
            ).slice(0, 6)}
            valueLabel="Formadores"
          />
        </View>
      </View>

      <SectionTable
        title="Experiencia como formador/a"
        rows={experienceRows(areaParticipants)}
        valueLabel="Formadores"
      />

      <SectionTable
        title="Participación en actividades"
        rows={activityRowsForParticipants(
          eventosConAsistentes,
          areaParticipants,
          eventos
        )}
        valueLabel="Participación"
        attendanceLabel="Asistencia"
      />
    </View>
  );
}

export default function GeneralReport({ data, contentFilter = ["general"] }) {
  const rawParticipantes = data?.tables?.participantes ?? [];
  const selectedDiscipline = normalizeText(data?.discipline ?? "general");

  const participantes =
    selectedDiscipline === "general"
      ? rawParticipantes
      : rawParticipantes.map((participante) => ({
        ...participante,
        disciplina:
          DISCIPLINE_LABEL[selectedDiscipline] ??
          participante?.disciplina,
      }));

  const universidades = data?.tables?.universidades ?? [];
  const eventos = data?.tables?.eventos ?? [];
  const regiones = data?.regiones ?? [];
  const formacion = data?.formacion ?? {};
  const eventosConAsistentes = data?.views?.eventosConAsistentes ?? [];
  const selectedContent = normalizeContentFilter(contentFilter);

  const showGeneral = selectedContent.includes("general");
  const showUniversidades =
    showGeneral || selectedContent.includes("universidades");
  const showGeography = showGeneral || selectedContent.includes("regiones");
  const showCarreras = showGeneral || selectedContent.includes("carreras");
  const isForeign = data?.country === "extranjero";

  const cuechCount = participantes.filter(
    (participante) => participante?.universidad_cuech
  ).length;
  const cruchCount = participantes.filter(
    (participante) => participante?.universidad_cruch
  ).length;

  const disciplineRows = countMany(participantes, (participante) =>
    splitValues(participante?.disciplina)
  );
  const programRows = countMany(participantes, (participante) =>
    splitValues(participante?.programa)
  );

  const titleRows = countBy(
    participantes,
    (participante) => participante?.titulo_profesional
  );

  const degreeRows = countBy(
    participantes,
    (participante) => participante?.grado
  );

  const activityRows = activityRowsFromEvents(eventos);

  const validRegions = regiones.filter(
    (region) => !isMissingValue(region?.region_name ?? region?.region)
  );
  const countries = Array.from(
    participantes.reduce((map, participante) => {
      const countryName = String(participante?.pais ?? "").trim();

      if (isMissingValue(countryName)) return map;

      const countryId = normalizeText(countryName);
      const current = map.get(countryId) ?? {
        id: countryId,
        pais: countryName,
        formadores_qtty: 0,
      };

      current.formadores_qtty += 1;
      map.set(countryId, current);

      return map;
    }, new Map()).values()
  ).sort(
    (a, b) =>
      b.formadores_qtty - a.formadores_qtty ||
      String(a.pais).localeCompare(String(b.pais), "es", {
        sensitivity: "base",
      })
  );
  const geographicAreas = isForeign ? countries : validRegions;
  const validUniversities = universidades.filter(
    (university) =>
      !isMissingValue(university?.universidad_full ?? university?.universidad)
  );

  const geographicRows = geographicAreas.map((area) => ({
    label: isForeign ? area.pais : area.region_name ?? area.region,
    value: area.formadores_qtty,
  }));

  const universityRows = validUniversities.map((university) => {
    const rawName = valueOrDash(
      university?.universidad_full ?? university?.universidad
    );

    return {
      label:
        rawName.includes(" ") &&
          (rawName === rawName.toUpperCase() ||
            rawName === rawName.toLowerCase())
          ? formatTitle(rawName)
          : rawName,
      value: university.formadores_qtty,
    };
  });

  const standardSections = [
    {
      title:
        "Pedagogía en Matemática",
      rows: standardRows(
        participantes,
        "estandar_media",
        "estandar_media_keys"
      ),
    },
    {
      title:
        "Pedagogía en Educación Básica - Matemática",
      rows: standardRows(
        participantes,
        "estandar_basica",
        "estandar_basica_keys"
      ),
    },
    {
      title:
        "Pedagogía en Educación Básica - Lenguaje",
      rows: standardRows(
        participantes,
        "estandar_lenguaje",
        "estandar_lenguaje_keys"
      ),
    },
  ].filter(({ rows }) => rows.length);

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <ReportStyle disciplina={data?.discipline} />

        <View style={styles.content}>
          <ReportHeader
            title="Reporte general RedFID"
            subtitle="Resumen institucional de participación"
          />

          <Text style={styles.subtitle}>Fecha de emisión: {fechaEmision}</Text>

          {showGeneral ? (
            <>
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Formadores en RedFID</Text>

                <View style={styles.grid}>
                  <SummaryCard
                    value={participantes.length}
                    label={plural(
                      participantes.length,
                      "Formador",
                      "Formadores"
                    )}
                  />

                  <SummaryCard
                    value={validUniversities.length}
                    label={plural(
                      validUniversities.length,
                      "Universidad",
                      "Universidades"
                    )}
                  />

                  <SummaryCard
                    value={geographicAreas.length}
                    label={
                      isForeign
                        ? plural(geographicAreas.length, "País", "Países")
                        : plural(geographicAreas.length, "Región", "Regiones")
                    }
                  />

                  {!isForeign ? (
                    <>
                      <SummaryCard
                        value={cuechCount}
                        label={plural(
                          cuechCount,
                          "Formador CUECH",
                          "Formadores CUECH"
                        )}
                      />

                      <SummaryCard
                        value={cruchCount}
                        label={plural(
                          cruchCount,
                          "Formador CRUCH",
                          "Formadores CRUCH"
                        )}
                      />
                    </>
                  ) : null}
                </View>

                <View style={styles.twoCols}>
                  <View style={styles.col}>
                    <SectionTable
                      title="Formadores por disciplina"
                      rows={disciplineRows.slice(0, 8)}
                      valueLabel="Formadores"
                    />
                  </View>

                  <View style={styles.col}>
                    <SectionTable
                      title="Formadores por programa"
                      rows={programRows.filter((row) =>
                        ["basica", "media", "parvularia"].some((program) =>
                          normalizeText(row.label).includes(program)
                        )
                      )}
                      valueLabel="Formadores"
                    />
                  </View>
                </View>
                <View style={styles.twoCols}>
                  <View style={styles.col}>
                    <SectionTable
                      title="Título profesional"
                      rows={titleRows}
                      valueLabel="Formadores"
                    />
                  </View>

                  <View style={styles.col}>
                    <SectionTable
                      title="Grado académico"
                      rows={degreeRows}
                      valueLabel="Formadores"
                    />
                  </View>
                </View>

                <SectionTable
                  title="Experiencia como formador/a"
                  rows={experienceRows(participantes)}
                  valueLabel="Formadores"
                />
              </View>

              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Actividades RedFID</Text>

                <View style={styles.grid}>
                  {activityRows.map((row) => (
                    <SummaryCard
                      key={row.label}
                      value={row.value}
                      label={row.label}
                    />
                  ))}
                </View>
              </View>

              <View style={styles.section}>
                <Text style={styles.sectionTitle}>
                  {isForeign ? "Formadores por país" : "Formadores por región"}
                </Text>

                <SectionTable
                  title={isForeign ? "Distribución por país" : "Distribución territorial"}
                  rows={geographicRows}
                  valueLabel="Formadores"
                />
              </View>

              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Universidades RedFID</Text>

                {validUniversities.map((university) => (
                  <UniversityCard
                    key={university.id}
                    university={university}
                    participantes={participantes}
                    eventosConAsistentes={eventosConAsistentes}
                    eventos={eventos}
                    showActivities={false}
                    isForeign={isForeign}
                  />
                ))}
              </View>
            </>
          ) : null}

          {!showGeneral && showUniversidades ? (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Detalle por universidad</Text>

              <View style={styles.grid}>
                <SummaryCard
                  value={validUniversities.length}
                  label="Universidades participantes"
                />

                <SummaryCard
                  value={participantes.length}
                  label="Formadores"
                />

                {!isForeign ? (
                  <>
                    <SummaryCard
                      value={
                        validUniversities.filter(
                          (university) => university?.universidad_cuech
                        ).length
                      }
                      label="Universidades CUECH"
                    />

                    <SummaryCard
                      value={
                        validUniversities.filter(
                          (university) => university?.universidad_cruch
                        ).length
                      }
                      label="Universidades CRUCH"
                    />
                  </>
                ) : null}
              </View>

              <SectionTable
                title="Formadores por universidad"
                rows={universityRows}
                valueLabel="Formadores"
              />

              {validUniversities.map((university) => (
                <UniversityCard
                  key={university.id}
                  university={university}
                  participantes={participantes}
                  eventosConAsistentes={eventosConAsistentes}
                  eventos={eventos}
                  isForeign={isForeign}
                />
              ))}
            </View>
          ) : null}

          {!showGeneral && showGeography ? (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>
                {isForeign ? "Detalle por país" : "Detalle por región"}
              </Text>

              <View style={styles.grid}>
                <SummaryCard
                  value={geographicAreas.length}
                  label={isForeign ? "Países representados" : "Regiones representadas"}
                />
                <SummaryCard
                  value={participantes.length}
                  label="Formadores"
                />
                <SummaryCard
                  value={validUniversities.length}
                  label="Universidades"
                />
              </View>

              <SectionTable
                title={isForeign ? "Formadores por país" : "Formadores por región"}
                rows={geographicRows}
                valueLabel="Formadores"
              />

              {geographicAreas.map((area) => (
                <GeographicCard
                  key={area.id ?? area.region_roman}
                  area={area}
                  participantes={participantes}
                  eventosConAsistentes={eventosConAsistentes}
                  eventos={eventos}
                  isForeign={isForeign}
                />
              ))}
            </View>
          ) : null}

          {!showGeneral && showCarreras ? (
            <>
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>
                  Perfil académico de formadores
                </Text>

                <View style={styles.grid}>
                  <SummaryCard
                    value={participantes.length}
                    label="Formadores"
                  />
                </View>

                <View style={styles.twoCols}>
                  <View style={styles.col}>
                    <SectionTable
                      title="Programas o carreras"
                      rows={programRows}
                      valueLabel="Formadores"
                    />
                  </View>

                  <View style={styles.col}>
                    <SectionTable
                      title="Disciplinas"
                      rows={disciplineRows}
                      valueLabel="Formadores"
                    />
                  </View>
                </View>



                <View style={styles.twoCols}>
                  <View style={styles.col}>
                    <SectionTable
                      title="Título profesional"
                      rows={titleRows}
                      valueLabel="Formadores"
                    />
                  </View>
                  <View style={styles.col}>
                    <SectionTable
                      title="Grado académico"
                      rows={(formacion?.grados ?? [])
                        .filter((row) => !isMissingValue(row?.id))
                        .map((row) => ({
                          label: row.id,
                          value: row.count,
                        }))}
                      valueLabel="Formadores"
                    />
                  </View>

                  <View style={styles.col}>
                    <SectionTable
                      title="Experiencia como formador/a"
                      rows={experienceRows(participantes)}
                      valueLabel="Formadores"
                    />
                  </View>
                </View>
              </View>

              {standardSections.length ? (
                <View style={styles.section}>
                  <Text style={styles.sectionTitle}>
                    Estándares disciplinarios
                  </Text>

                  {standardSections.map(({ title, rows }) => (
                    <SectionTable
                      key={title}
                      title={title}
                      rows={rows}
                      valueLabel="Formadores"
                    />
                  ))}
                </View>
              ) : null}
            </>
          ) : null}
          {!showGeneral &&
            !showUniversidades &&
            !showGeography &&
            !showCarreras ? (
            <Text style={styles.empty}>
              No hay contenido seleccionado para el reporte.
            </Text>
          ) : null}
        </View>

        <Text
          fixed
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          style={styles.pageNumber}
        />
      </Page>
    </Document>
  );
}
